
import React, { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash, Edit, Save, X } from "lucide-react";
import { toast } from "sonner";

type Contact = {
  id: string;
  name: string;
  email: string;
  last_contact: string;
  source: string;
  notes?: string;
};

type ContactsManagementProps = {
  companyId: string;
};

export function ContactsManagement({ companyId }: ContactsManagementProps) {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [newContact, setNewContact] = useState({ name: '', email: '', notes: '' });
  const [editingContact, setEditingContact] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{ name: string; email: string; notes: string }>({ 
    name: '', 
    email: '', 
    notes: '' 
  });

  useEffect(() => {
    fetchContacts();
  }, [companyId]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('contacts')
        .select('*')
        .eq('company_id', companyId)
        .order('last_contact', { ascending: false });

      if (error) throw error;
      setContacts(data || []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Failed to fetch contacts');
    } finally {
      setLoading(false);
    }
  };

  const addContact = async () => {
    if (!newContact.name || !newContact.email) {
      toast.error('Name and email are required');
      return;
    }

    try {
      const { error } = await supabase
        .from('contacts')
        .insert({
          company_id: companyId,
          name: newContact.name,
          email: newContact.email,
          notes: newContact.notes,
          source: 'manual'
        });

      if (error) throw error;
      
      fetchContacts();
      setNewContact({ name: '', email: '', notes: '' });
      toast.success('Contact added successfully');
    } catch (error) {
      console.error('Error adding contact:', error);
      toast.error('Failed to add contact');
    }
  };

  const deleteContact = async (contactId: string) => {
    try {
      console.log('Deleting contact with ID:', contactId);
      
      // It appears that the match criteria might be incorrect
      // Let's debug more thoroughly and ensure we're matching the right record
      
      const { error, data } = await supabase
        .from('contacts')
        .delete()
        .eq('id', contactId)
        .select(); // Add select() to return the deleted records

      if (error) {
        console.error('Supabase delete error:', error);
        throw error;
      }

      console.log('Delete operation completed. Deleted records:', data);
      
      if (!data || data.length === 0) {
        console.warn('No records were deleted. Contact may not exist in the database.');
        // Try fetching the contact to verify if it exists
        const { data: checkData } = await supabase
          .from('contacts')
          .select('id')
          .eq('id', contactId);
          
        if (checkData && checkData.length > 0) {
          console.error('Contact still exists in the database!');
          toast.error('Failed to delete contact: Record still exists');
          return;
        }
      }
      
      // Update the UI
      setContacts(prevContacts => prevContacts.filter(contact => contact.id !== contactId));
      toast.success('Contact deleted successfully');
    } catch (error: any) {
      console.error('Error deleting contact:', error);
      toast.error(`Failed to delete contact: ${error.message || 'Unknown error'}`);
    }
  };

  const startEditing = (contact: Contact) => {
    setEditingContact(contact.id);
    setEditForm({
      name: contact.name,
      email: contact.email,
      notes: contact.notes || ''
    });
  };

  const cancelEditing = () => {
    setEditingContact(null);
  };

  const updateContact = async (contactId: string) => {
    if (!editForm.name || !editForm.email) {
      toast.error('Name and email are required');
      return;
    }

    try {
      const { error } = await supabase
        .from('contacts')
        .update({
          name: editForm.name,
          email: editForm.email,
          notes: editForm.notes,
          updated_at: new Date().toISOString()
        })
        .eq('id', contactId);

      if (error) throw error;
      
      // Update the local state
      setContacts(prevContacts => 
        prevContacts.map(contact => 
          contact.id === contactId 
            ? { ...contact, name: editForm.name, email: editForm.email, notes: editForm.notes } 
            : contact
        )
      );
      
      setEditingContact(null);
      toast.success('Contact updated successfully');
    } catch (error: any) {
      console.error('Error updating contact:', error);
      toast.error(`Failed to update contact: ${error.message || 'Unknown error'}`);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="col-span-1 bg-muted/30 p-4 rounded-lg">
          <h3 className="text-lg font-medium mb-4">Add New Contact</h3>
          <div className="space-y-2">
            <Input 
              placeholder="Name" 
              value={newContact.name}
              onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
            />
            <Input 
              placeholder="Email" 
              type="email"
              value={newContact.email}
              onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
            />
            <Input 
              placeholder="Notes (optional)" 
              value={newContact.notes}
              onChange={(e) => setNewContact({ ...newContact, notes: e.target.value })}
            />
            <Button onClick={addContact} className="w-full">
              <Plus className="mr-2 h-4 w-4" /> Add Contact
            </Button>
          </div>
        </div>
        <div className="col-span-1 md:col-span-2">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Last Contact</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center">Loading contacts...</TableCell>
                </TableRow>
              ) : contacts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground">
                    No contacts found
                  </TableCell>
                </TableRow>
              ) : (
                contacts.map((contact) => (
                  <TableRow key={contact.id}>
                    {editingContact === contact.id ? (
                      <>
                        <TableCell>
                          <Input 
                            value={editForm.name}
                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                          />
                        </TableCell>
                        <TableCell>
                          <Input 
                            value={editForm.email}
                            onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                          />
                        </TableCell>
                        <TableCell>
                          {new Date(contact.last_contact).toLocaleDateString()}
                        </TableCell>
                        <TableCell>{contact.source}</TableCell>
                        <TableCell>
                          <Input 
                            value={editForm.notes}
                            onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                          />
                        </TableCell>
                        <TableCell className="flex space-x-1">
                          <Button 
                            variant="default" 
                            size="icon" 
                            onClick={() => updateContact(contact.id)}
                          >
                            <Save className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="icon" 
                            onClick={cancelEditing}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>{contact.name}</TableCell>
                        <TableCell>{contact.email}</TableCell>
                        <TableCell>
                          {new Date(contact.last_contact).toLocaleDateString()}
                        </TableCell>
                        <TableCell>{contact.source}</TableCell>
                        <TableCell className="max-w-[200px] truncate">
                          {contact.notes}
                        </TableCell>
                        <TableCell className="flex space-x-1">
                          <Button 
                            variant="outline" 
                            size="icon" 
                            onClick={() => startEditing(contact)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="destructive" 
                            size="icon" 
                            onClick={() => deleteContact(contact.id)}
                          >
                            <Trash className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
